import React, { useState, useCallback, useMemo, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Slider } from '../components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  ChevronDown, ChevronUp, FlaskConical, ArrowRight,
  TrendingUp, TrendingDown, Minus, CalendarDays, Loader2, RotateCcw,
  Plus, X, Sparkles, Pencil
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ── Modifier semantics (match Estimator exactly) ──
const READINESS_FACTORS = {
  governance: 'Governance Maturity',
  content_readiness: 'Content Readiness',
  client_capability: 'Client Team Capability',
  change_readiness: 'Change Readiness',
};
const READINESS_LABELS = ['Very Weak', 'Weak', 'Neutral', 'Strong', 'Very Strong'];
const READINESS_VALUES = [1.4, 1.2, 1.0, 0.9, 0.8];

const COMPLEXITY_FACTORS = {
  integration_complexity: 'Integration Complexity',
  org_complexity: 'Organizational Complexity',
  timeline_pressure: 'Timeline Pressure',
};
const COMPLEXITY_LABELS = ['Low', 'Moderate', 'Average', 'High', 'Very High'];
const COMPLEXITY_VALUES = [0.8, 0.9, 1.0, 1.2, 1.4];

const IS_READINESS = new Set(Object.keys(READINESS_FACTORS));
const ALL_LABELS = { ...READINESS_FACTORS, ...COMPLEXITY_FACTORS };

const IMPL_TYPES = [
  { value: 'Corporate', label: 'Corporate' },
  { value: 'EXE_Ecommerce', label: 'EXE / Ecommerce' },
  { value: 'Workforce_Enablement', label: 'Workforce Enablement' },
];
const HOSTING_MODELS = [
  { value: 'public_cloud', label: 'Public Cloud' },
  { value: 'private_cloud', label: 'Private Cloud' },
];

function valueToIndex(val, values) {
  let closest = 0, minDiff = Math.abs(val - values[0]);
  for (let i = 1; i < values.length; i++) {
    const diff = Math.abs(val - values[i]);
    if (diff < minDiff) { minDiff = diff; closest = i; }
  }
  return closest;
}

function getLabel(key, val) {
  const isRead = IS_READINESS.has(key);
  return (isRead ? READINESS_LABELS : COMPLEXITY_LABELS)[valueToIndex(val, isRead ? READINESS_VALUES : COMPLEXITY_VALUES)];
}

function DeltaBadge({ delta, suffix = '' }) {
  if (!delta || delta === 0) return <span className="text-[10px] text-slate-400">—</span>;
  const isPos = delta > 0;
  return (
    <span className={`text-[10px] font-semibold ${isPos ? 'text-red-600' : 'text-emerald-600'}`}>
      {isPos ? '+' : ''}{typeof delta === 'number' ? (Number.isInteger(delta) ? delta : delta.toFixed(1)) : delta}{suffix}
    </span>
  );
}

function ModifierSlider({ factorKey, label, originalValue, currentValue, onChange }) {
  const isReadiness = IS_READINESS.has(factorKey);
  const values = isReadiness ? READINESS_VALUES : COMPLEXITY_VALUES;
  const labels = isReadiness ? READINESS_LABELS : COMPLEXITY_LABELS;
  const idx = valueToIndex(currentValue, values);
  const origIdx = valueToIndex(originalValue, values);
  const changed = idx !== origIdx;

  return (
    <div className={`rounded-lg p-2.5 transition-colors ${changed ? 'bg-violet-50/70 ring-1 ring-violet-200' : 'bg-slate-50/50'}`} data-testid={`whatif-mod-${factorKey}`}>
      <div className="flex items-center justify-between mb-1.5">
        <span className={`text-[10px] font-medium ${changed ? 'text-violet-700' : 'text-slate-600'}`}>{label}</span>
        <div className="flex items-center gap-1.5">
          {changed && <span className="text-[9px] text-slate-400 line-through">{labels[origIdx]}</span>}
          <Badge variant={changed ? 'default' : 'outline'}
            className={`text-[9px] px-1.5 py-0 ${changed ? 'bg-violet-600 text-white' : 'bg-white text-slate-500'}`}>
            {labels[idx]}
          </Badge>
        </div>
      </div>
      <Slider value={[idx]} onValueChange={([v]) => onChange(factorKey, values[v])} min={0} max={4} step={1} className="my-1" />
      <div className="flex justify-between text-[8px] text-slate-400 mt-0.5 px-0.5">
        <span>{isReadiness ? `${labels[0]} (more effort)` : `${labels[0]} (less effort)`}</span>
        <span>{isReadiness ? `${labels[4]} (less effort)` : `${labels[4]} (more effort)`}</span>
      </div>
    </div>
  );
}

// ── Side-by-Side metric row ──
function MetricRow({ label, original, whatif, suffix = '', prefix = '' }) {
  const delta = typeof whatif === 'number' && typeof original === 'number' ? whatif - original : 0;
  const fmtVal = (v) => `${prefix}${typeof v === 'number' ? v.toLocaleString(undefined, { maximumFractionDigits: 1 }) : v}${suffix}`;
  return (
    <div className="grid grid-cols-[1fr_1fr_1fr_80px] gap-2 items-center py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-[10px] font-medium text-slate-600">{label}</span>
      <span className="text-[11px] text-slate-700 tabular-nums text-right">{fmtVal(original)}</span>
      <span className="text-[11px] font-semibold text-[#0F2040] tabular-nums text-right">{fmtVal(whatif)}</span>
      <div className="text-right"><DeltaBadge delta={delta} suffix={suffix} /></div>
    </div>
  );
}

export default function WhatIfSimulator({ estimateId, estimate, onApplyTimeline, onResultChange, onNarrativeChange }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [narrative, setNarrative] = useState(null);
  const [componentLibrary, setComponentLibrary] = useState([]);
  const [showAddComp, setShowAddComp] = useState(false);
  const [addFilterText, setAddFilterText] = useState('');

  // What-If overrides
  const [excludedComponents, setExcludedComponents] = useState(new Set());
  const [addedComponents, setAddedComponents] = useState([]);
  const [effortOverrides, setEffortOverrides] = useState({});
  const [editingEffort, setEditingEffort] = useState(null);
  const [modifierOverrides, setModifierOverrides] = useState({});
  const [hostingOverride, setHostingOverride] = useState('');
  const [implTypeOverride, setImplTypeOverride] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [compFilterText, setCompFilterText] = useState('');

  // Fetch component library once
  useEffect(() => {
    if (open && componentLibrary.length === 0) {
      axios.get(`${API_URL}/api/planner/component-library`, { withCredentials: true })
        .then(res => setComponentLibrary(res.data.components || []))
        .catch(() => {});
    }
  }, [open, componentLibrary.length]);

  const components = useMemo(() => {
    const breakdown = estimate?.results?.component_breakdown || [];
    return breakdown.map(c => ({
      name: c.name || c.component_name,
      group: c.group,
      role: c.role,
      effort: c.final_effort,
      required: c.required,
    }));
  }, [estimate]);

  const existingNames = useMemo(() => new Set(components.map(c => c.name)), [components]);

  const groups = useMemo(() => {
    const g = {};
    // Existing components
    components.forEach(c => {
      if (!g[c.group]) g[c.group] = [];
      g[c.group].push({ ...c, isAdded: false });
    });
    // Added components
    addedComponents.forEach(c => {
      if (!g[c.group]) g[c.group] = [];
      g[c.group].push({ ...c, isAdded: true });
    });
    return Object.entries(g).sort((a, b) => a[0].localeCompare(b[0]));
  }, [components, addedComponents]);

  const modifiers = useMemo(() => estimate?.modifiers || {}, [estimate]);

  // Memoized filter — toLowerCase + filter-and-rebuild only fires when
  // the search text or the underlying groups change, not on every render.
  // Must be declared BEFORE any early return (rules-of-hooks).
  const filteredGroups = useMemo(() => {
    if (!compFilterText) return groups;
    const needle = compFilterText.toLowerCase();
    return groups
      .map(([grp, comps]) => [grp, comps.filter((c) => c.name.toLowerCase().includes(needle))])
      .filter(([, c]) => c.length > 0);
  }, [groups, compFilterText]);

  const hasChanges = useMemo(() => {
    return excludedComponents.size > 0 || addedComponents.length > 0 ||
      Object.keys(effortOverrides).length > 0 || Object.keys(modifierOverrides).length > 0 ||
      (hostingOverride && hostingOverride !== estimate?.hosting_model) ||
      (implTypeOverride && implTypeOverride !== estimate?.implementation_type) ||
      startDate || endDate;
  }, [excludedComponents, addedComponents, effortOverrides, modifierOverrides, hostingOverride, implTypeOverride, startDate, endDate, estimate]);

  const resetAll = useCallback(() => {
    setExcludedComponents(new Set());
    setAddedComponents([]);
    setEffortOverrides({});
    setEditingEffort(null);
    setModifierOverrides({});
    setHostingOverride('');
    setImplTypeOverride('');
    setStartDate('');
    setEndDate('');
    setResult(null);
    setNarrative(null);
    if (onResultChange) onResultChange(null);
    onNarrativeChange?.(null);
    setShowAddComp(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleComponent = useCallback((name) => {
    setExcludedComponents(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  }, []);

  const removeAddedComponent = useCallback((name) => {
    setAddedComponents(prev => prev.filter(c => c.name !== name));
  }, []);

  const addComponent = useCallback((comp) => {
    const complexity = estimate?.complexity || 'standard';
    const effort = comp.base_hours?.[complexity] || comp.base_hours?.standard || 10;
    setAddedComponents(prev => [...prev, {
      name: comp.name, group: comp.group, role: comp.role, effort, required: false,
    }]);
    setShowAddComp(false);
    setAddFilterText('');
  }, [estimate]);

  const handleModifierChange = useCallback((key, val) => {
    const original = typeof modifiers[key] === 'number' ? modifiers[key] : 1.0;
    if (val === original) {
      setModifierOverrides(prev => { const n = { ...prev }; delete n[key]; return n; });
    } else {
      setModifierOverrides(prev => ({ ...prev, [key]: val }));
    }
  }, [modifiers]);

  const setEffortOverride = useCallback((name, val) => {
    const comp = components.find(c => c.name === name);
    if (comp && Math.abs(val - comp.effort) < 0.1) {
      setEffortOverrides(prev => { const n = { ...prev }; delete n[name]; return n; });
    } else {
      setEffortOverrides(prev => ({ ...prev, [name]: val }));
    }
  }, [components]);

  const runScenario = useCallback(async () => {
    if (!estimateId) return;
    setLoading(true);
    setNarrative(null);
    onNarrativeChange?.(null);
    try {
      const payload = { estimate_id: estimateId };
      if (excludedComponents.size > 0) payload.excluded_components = [...excludedComponents];
      if (addedComponents.length > 0) payload.added_components = addedComponents.map(c => ({ name: c.name, group: c.group, role: c.role, effort: c.effort }));
      if (Object.keys(effortOverrides).length > 0) payload.effort_overrides = effortOverrides;
      if (Object.keys(modifierOverrides).length > 0) payload.modifier_overrides = modifierOverrides;
      if (hostingOverride && hostingOverride !== estimate?.hosting_model) payload.hosting_model_override = hostingOverride;
      if (implTypeOverride && implTypeOverride !== estimate?.implementation_type) payload.implementation_type_override = implTypeOverride;
      if (startDate) payload.start_date = startDate;
      if (endDate) payload.end_date = endDate;

      const res = await axios.post(`${API_URL}/api/planner/what-if`, payload, { withCredentials: true });
      setResult(res.data);
      if (onResultChange) onResultChange(res.data);
      toast.success('What-If scenario calculated');
    } catch {
      toast.error('Failed to calculate scenario');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, excludedComponents, addedComponents, effortOverrides, modifierOverrides, hostingOverride, implTypeOverride, startDate, endDate, estimate]);

  const generateNarrative = useCallback(async () => {
    if (!result) return;
    setNarrativeLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/planner/what-if/narrative`, {
        whatif_result: result,
        estimate_name: estimate?.estimate_name || '',
        user_changes: {
          excluded_components: [...excludedComponents],
          added_components: addedComponents.map(c => ({ name: c.name, group: c.group, effort: c.effort })),
          effort_overrides: effortOverrides,
          modifier_overrides: modifierOverrides,
          hosting_override: hostingOverride !== estimate?.hosting_model ? hostingOverride : null,
          impl_type_override: implTypeOverride !== estimate?.implementation_type ? implTypeOverride : null,
        },
      }, { withCredentials: true });
      setNarrative(res.data.narrative);
      onNarrativeChange?.(res.data.narrative);
      toast.success('AI analysis generated');
    } catch {
      toast.error('Failed to generate AI analysis');
    } finally {
      setNarrativeLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result, estimate, excludedComponents, addedComponents, effortOverrides, modifierOverrides, hostingOverride, implTypeOverride]);

  if (!estimate) return null;

  // Available components to add (not already in estimate or added list)
  const addedNames = new Set(addedComponents.map(c => c.name));
  const availableToAdd = componentLibrary.filter(c =>
    !existingNames.has(c.name) && !addedNames.has(c.name) &&
    (!addFilterText || c.name.toLowerCase().includes(addFilterText.toLowerCase()))
  );
  const availableGroups = {};
  availableToAdd.forEach(c => {
    if (!availableGroups[c.group]) availableGroups[c.group] = [];
    availableGroups[c.group].push(c);
  });

  return (
    <Card className="border-dashed border-2 border-violet-200/60" data-testid="what-if-simulator">
      {/* Header with Reset */}
      <CardHeader className="cursor-pointer pb-2 hover:bg-slate-50/50 transition-colors" onClick={() => setOpen(!open)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FlaskConical className="w-4 h-4 text-violet-500" />
            <CardTitle className="text-sm font-semibold text-[#0F2040]">What-If Scenario Simulator</CardTitle>
            {hasChanges && <Badge className="text-[9px] bg-violet-100 text-violet-700 border-violet-200" variant="outline">Modified</Badge>}
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && open && (
              <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); resetAll(); }} className="h-6 px-2 text-[10px] text-slate-500 hover:text-red-600" data-testid="whatif-reset-top">
                <RotateCcw className="w-3 h-3 mr-1" />Reset
              </Button>
            )}
            {open ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
          </div>
        </div>
      </CardHeader>

      {open && (
        <CardContent className="space-y-4 pt-0">
          {/* ─── Implementation, Hosting & Dates ─── */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Implementation Type</Label>
              <div className="flex items-center gap-2">
                <Select value={implTypeOverride || estimate.implementation_type} onValueChange={v => setImplTypeOverride(v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="whatif-impl-type-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>{IMPL_TYPES.map(t => <SelectItem key={t.value} value={t.value} className="text-xs">{t.label}</SelectItem>)}</SelectContent>
                </Select>
                {implTypeOverride && implTypeOverride !== estimate.implementation_type && <span className="text-[9px] text-slate-400 shrink-0">was: {estimate.implementation_type}</span>}
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Hosting Model</Label>
              <div className="flex items-center gap-2">
                <Select value={hostingOverride || estimate.hosting_model || 'public_cloud'} onValueChange={v => setHostingOverride(v)}>
                  <SelectTrigger className="h-8 text-xs" data-testid="whatif-hosting-trigger"><SelectValue /></SelectTrigger>
                  <SelectContent>{HOSTING_MODELS.map(h => <SelectItem key={h.value} value={h.value} className="text-xs">{h.label}</SelectItem>)}</SelectContent>
                </Select>
                {hostingOverride && hostingOverride !== (estimate.hosting_model || 'public_cloud') && <span className="text-[9px] text-slate-400 shrink-0">was: {estimate.hosting_model}</span>}
              </div>
            </div>
            <div>
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-1 block">Date Range (Optional)</Label>
              <div className="flex items-center gap-1">
                <div className="relative flex-1">
                  <CalendarDays className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                  <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="w-full h-8 pl-6 pr-1 text-[10px] border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300" data-testid="whatif-start-date" />
                </div>
                <ArrowRight className="w-3 h-3 text-slate-400 shrink-0" />
                <div className="relative flex-1">
                  <CalendarDays className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-slate-400 pointer-events-none" />
                  <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="w-full h-8 pl-6 pr-1 text-[10px] border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300" data-testid="whatif-end-date" />
                </div>
              </div>
            </div>
          </div>

          {/* ─── Readiness Factors ─── */}
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
              Readiness Factors <span className="ml-2 font-normal text-[9px] text-slate-400">Higher readiness = less effort</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {Object.entries(READINESS_FACTORS).map(([key, label]) => (
                <ModifierSlider key={key} factorKey={key} label={label}
                  originalValue={typeof modifiers[key] === 'number' ? modifiers[key] : 1.0}
                  currentValue={modifierOverrides[key] !== undefined ? modifierOverrides[key] : (typeof modifiers[key] === 'number' ? modifiers[key] : 1.0)}
                  onChange={handleModifierChange} />
              ))}
            </div>
          </div>

          {/* ─── Complexity Factors ─── */}
          <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide mb-2 block">
              Complexity &amp; Pressure Factors <span className="ml-2 font-normal text-[9px] text-slate-400">Higher complexity = more effort</span>
            </Label>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {Object.entries(COMPLEXITY_FACTORS).map(([key, label]) => (
                <ModifierSlider key={key} factorKey={key} label={label}
                  originalValue={typeof modifiers[key] === 'number' ? modifiers[key] : 1.0}
                  currentValue={modifierOverrides[key] !== undefined ? modifierOverrides[key] : (typeof modifiers[key] === 'number' ? modifiers[key] : 1.0)}
                  onChange={handleModifierChange} />
              ))}
            </div>
          </div>

          {/* ─── Components (with add/remove/edit effort) ─── */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[10px] font-semibold text-slate-500 uppercase tracking-wide">Components</Label>
              <div className="flex items-center gap-2">
                <input type="text" placeholder="Filter..." value={compFilterText} onChange={e => setCompFilterText(e.target.value)}
                  className="h-6 w-28 px-2 text-[10px] border rounded-md focus:outline-none focus:ring-1 focus:ring-violet-300" data-testid="whatif-comp-filter" />
                <Button variant="outline" size="sm" onClick={() => setShowAddComp(!showAddComp)}
                  className="h-6 px-2 text-[10px] gap-1" data-testid="whatif-add-comp-btn">
                  <Plus className="w-3 h-3" />Add
                </Button>
              </div>
            </div>

            {/* Add Component Panel */}
            {showAddComp && (
              <div className="border rounded-lg p-2 mb-2 bg-violet-50/30" data-testid="whatif-add-comp-panel">
                <input type="text" placeholder="Search components to add..." value={addFilterText} onChange={e => setAddFilterText(e.target.value)}
                  className="w-full h-7 px-2 text-[10px] border rounded-md mb-2 focus:outline-none focus:ring-1 focus:ring-violet-300" data-testid="whatif-add-comp-search" />
                <div className="max-h-36 overflow-y-auto space-y-1.5">
                  {Object.entries(availableGroups).map(([grp, comps]) => (
                    <div key={grp}>
                      <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-0.5">{grp}</p>
                      {comps.map(c => (
                        <div key={c.name} className="flex items-center justify-between py-0.5 px-2 hover:bg-violet-100/50 rounded cursor-pointer"
                          onClick={() => addComponent(c)} data-testid={`whatif-add-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
                          <span className="text-[10px] text-slate-700">{c.name}</span>
                          <span className="text-[9px] text-slate-400">{c.role} · {c.base_hours?.[estimate?.complexity || 'standard'] || '?'}h</span>
                        </div>
                      ))}
                    </div>
                  ))}
                  {Object.keys(availableGroups).length === 0 && <p className="text-[10px] text-slate-400 text-center py-2">No components available to add</p>}
                </div>
              </div>
            )}

            {/* Component List */}
            <div className="max-h-56 overflow-y-auto space-y-2 pr-1">
              {filteredGroups.map(([grp, comps]) => (
                <div key={grp}>
                  <p className="text-[9px] font-bold text-slate-400 uppercase tracking-wider mb-1">{grp}</p>
                  <div className="space-y-0.5">
                    {comps.map(c => {
                      const excluded = excludedComponents.has(c.name);
                      const hasEffortOverride = effortOverrides[c.name] !== undefined;
                      const displayEffort = hasEffortOverride ? effortOverrides[c.name] : c.effort;
                      const isEditing = editingEffort === c.name;

                      return (
                        <div key={c.name}
                          className={`flex items-center justify-between py-0.5 px-2 rounded transition-colors ${
                            c.isAdded ? 'bg-emerald-50/60' : excluded ? 'bg-red-50/60' : hasEffortOverride ? 'bg-amber-50/60' : 'hover:bg-slate-50'
                          }`}
                          data-testid={`whatif-comp-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            {c.isAdded ? (
                              <button onClick={() => removeAddedComponent(c.name)} className="text-red-400 hover:text-red-600 p-0.5" data-testid={`whatif-remove-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                <X className="w-3 h-3" />
                              </button>
                            ) : (
                              <Switch checked={!excluded} onCheckedChange={() => toggleComponent(c.name)} disabled={c.required} className="scale-75" />
                            )}
                            <span className={`text-[10px] truncate ${excluded ? 'line-through text-slate-400' : 'text-slate-700'}`}>{c.name}</span>
                            {c.isAdded && <Badge className="text-[8px] px-1 py-0 bg-emerald-100 text-emerald-700 border-emerald-200" variant="outline">New</Badge>}
                            {c.required && <Badge variant="outline" className="text-[8px] px-1 py-0 text-slate-400">Req</Badge>}
                          </div>
                          <div className="flex items-center gap-1.5 shrink-0">
                            {hasEffortOverride && !c.isAdded && <span className="text-[9px] text-slate-400 line-through">{c.effort.toFixed(1)}h</span>}
                            {isEditing ? (
                              <input type="number" autoFocus min={0} step={1}
                                defaultValue={Math.round(displayEffort)}
                                className="w-16 h-5 px-1 text-[10px] border rounded text-right focus:outline-none focus:ring-1 focus:ring-violet-300"
                                onBlur={(e) => {
                                  const v = parseFloat(e.target.value);
                                  if (!isNaN(v) && v >= 0) {
                                    if (c.isAdded) {
                                      setAddedComponents(prev => prev.map(ac => ac.name === c.name ? { ...ac, effort: v } : ac));
                                    } else {
                                      setEffortOverride(c.name, v);
                                    }
                                  }
                                  setEditingEffort(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') e.target.blur();
                                  if (e.key === 'Escape') setEditingEffort(null);
                                }}
                                data-testid={`whatif-effort-input-${c.name.replace(/\s+/g, '-').toLowerCase()}`}
                              />
                            ) : (
                              <button onClick={() => !excluded && setEditingEffort(c.name)} disabled={excluded}
                                className={`flex items-center gap-0.5 text-[10px] tabular-nums ${excluded ? 'text-red-400 line-through cursor-not-allowed' : hasEffortOverride ? 'text-amber-700 font-semibold' : 'text-slate-500 hover:text-violet-600'}`}
                                data-testid={`whatif-effort-edit-${c.name.replace(/\s+/g, '-').toLowerCase()}`}>
                                {displayEffort.toFixed(1)}h
                                {!excluded && <Pencil className="w-2.5 h-2.5 opacity-40" />}
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* ─── Action Buttons ─── */}
          <div className="flex items-center gap-2 pt-1 border-t">
            <Button onClick={runScenario} disabled={loading} className="flex-1 bg-violet-600 hover:bg-violet-700 text-white text-xs h-8" data-testid="whatif-run-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <FlaskConical className="w-3.5 h-3.5 mr-1" />}
              Run Scenario
            </Button>
            <Button variant="outline" onClick={resetAll} className="text-xs h-8 px-3" data-testid="whatif-reset-btn">
              <RotateCcw className="w-3 h-3 mr-1" />Reset
            </Button>
          </div>

          {/* ─── Side-by-Side Results ─── */}
          {result && (
            <div className="space-y-3 pt-2 border-t" data-testid="whatif-results">
              {/* Side-by-side comparison table */}
              <div className="bg-white border rounded-lg overflow-hidden" data-testid="whatif-side-by-side">
                <div className="grid grid-cols-[1fr_1fr_1fr_80px] gap-2 px-3 py-2 bg-slate-100 border-b">
                  <span className="text-[9px] font-bold text-slate-500 uppercase">Metric</span>
                  <span className="text-[9px] font-bold text-slate-500 uppercase text-right">Original</span>
                  <span className="text-[9px] font-bold text-violet-600 uppercase text-right">What-If</span>
                  <span className="text-[9px] font-bold text-slate-500 uppercase text-right">Delta</span>
                </div>
                <div className="px-3 py-1">
                  <MetricRow label="Total Effort" original={result.diff.effort.original} whatif={result.diff.effort.whatif} suffix=" hrs" />
                  <MetricRow label="Duration" original={result.diff.duration.original} whatif={result.diff.duration.whatif} suffix="w" />
                  <MetricRow label="Total Cost" original={result.diff.cost.original} whatif={result.diff.cost.whatif} prefix="$" />
                  <MetricRow label="Peak FTE" original={result.diff.peak_fte.original} whatif={result.diff.peak_fte.whatif} />
                </div>
              </div>

              {/* Changes summary */}
              <div className="space-y-1.5">
                <div className="flex flex-wrap gap-1.5">
                  {result.whatif.implementation_type !== result.original.implementation_type && (
                    <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">Impl: {result.original.implementation_type} &rarr; {result.whatif.implementation_type}</Badge>
                  )}
                  {result.whatif.hosting_model !== result.original.hosting_model && (
                    <Badge variant="outline" className="text-[9px] bg-violet-50 text-violet-700 border-violet-200">Hosting: {result.original.hosting_model} &rarr; {result.whatif.hosting_model}</Badge>
                  )}
                  {result.whatif.excluded_components?.length > 0 && (
                    <Badge variant="outline" className="text-[9px] bg-red-50 text-red-700 border-red-200">{result.whatif.excluded_components.length} excluded</Badge>
                  )}
                  {result.whatif.start_date && result.whatif.end_date && (
                    <Badge variant="outline" className="text-[9px] bg-blue-50 text-blue-700 border-blue-200">{result.whatif.start_date} &rarr; {result.whatif.end_date}</Badge>
                  )}
                </div>

                {/* Factor changes */}
                {result.original.modifiers && result.whatif.modifiers && (() => {
                  const changes = Object.keys(ALL_LABELS).filter(k => result.original.modifiers[k] !== result.whatif.modifiers[k]);
                  if (changes.length === 0) return null;
                  return (
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-1.5">Factor Changes</p>
                      <div className="space-y-1">
                        {changes.map(k => (
                          <div key={k} className="flex items-center justify-between text-[10px]">
                            <span className="text-slate-600">{ALL_LABELS[k]}</span>
                            <div className="flex items-center gap-1.5">
                              <span className="text-slate-400">{getLabel(k, result.original.modifiers[k])} ({result.original.modifiers[k]})</span>
                              <ArrowRight className="w-2.5 h-2.5 text-slate-300" />
                              <span className="font-semibold text-violet-700">{getLabel(k, result.whatif.modifiers[k])} ({result.whatif.modifiers[k]})</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* Component changes table */}
                {result.component_diff && (() => {
                  const changed = result.component_diff.filter(c => c.delta !== 0);
                  if (changed.length === 0) return null;
                  return (
                    <div className="bg-slate-50 rounded-lg p-2.5">
                      <p className="text-[9px] text-slate-400 uppercase tracking-wide mb-1.5">Component Changes</p>
                      <div className="max-h-32 overflow-y-auto space-y-0.5">
                        {changed.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta)).map(c => (
                          <div key={c.name} className="flex items-center justify-between text-[10px] py-0.5">
                            <span className={`truncate mr-2 ${c.excluded ? 'text-red-500 line-through' : c.original_effort === 0 ? 'text-emerald-600' : 'text-slate-600'}`}>
                              {c.name}
                            </span>
                            <div className="flex items-center gap-1.5 shrink-0 tabular-nums">
                              <span className="text-slate-400">{c.original_effort}h</span>
                              <ArrowRight className="w-2.5 h-2.5 text-slate-300" />
                              <span className="font-semibold text-[#0F2040]">{c.whatif_effort}h</span>
                              <DeltaBadge delta={c.delta} suffix="h" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Action row: Apply + AI */}
              <div className="flex items-center gap-2">
                {onApplyTimeline && (
                  <Button onClick={() => { onApplyTimeline(result.whatif_timeline); toast.success('What-If timeline applied'); }}
                    variant="outline" className="flex-1 text-xs h-8 border-violet-300 text-violet-700 hover:bg-violet-50" data-testid="whatif-apply-btn">
                    Apply to Planner
                  </Button>
                )}
                <Button onClick={generateNarrative} disabled={narrativeLoading}
                  variant="outline" className="flex-1 text-xs h-8 border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="whatif-ai-narrative-btn">
                  {narrativeLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
                  AI Impact Analysis
                </Button>
              </div>

              {/* AI Narrative */}
              {narrative && (
                <div className="border rounded-lg overflow-hidden" data-testid="whatif-ai-narrative">
                  <div className="bg-amber-50 px-3 py-2 border-b flex items-center gap-2">
                    <Sparkles className="w-3.5 h-3.5 text-amber-600" />
                    <span className="text-[10px] font-semibold text-amber-800 uppercase tracking-wide">AI Impact Analysis</span>
                  </div>
                  <div className="p-3 space-y-3 text-[11px] text-slate-700 leading-relaxed">
                    {narrative.impact_summary && (
                      <div><p className="text-[9px] font-bold text-slate-500 uppercase mb-1">Impact Summary</p><p>{narrative.impact_summary}</p></div>
                    )}
                    {narrative.change_breakdown && (
                      <div><p className="text-[9px] font-bold text-indigo-600 uppercase mb-1">Change-by-Change Breakdown</p><p className="whitespace-pre-line">{narrative.change_breakdown}</p></div>
                    )}
                    {narrative.risk_assessment && (
                      <div><p className="text-[9px] font-bold text-red-500 uppercase mb-1">Risk Assessment</p><p>{narrative.risk_assessment}</p></div>
                    )}
                    {narrative.cost_implications && (
                      <div><p className="text-[9px] font-bold text-emerald-600 uppercase mb-1">Cost Implications</p><p>{narrative.cost_implications}</p></div>
                    )}
                    {narrative.timeline_impact && (
                      <div><p className="text-[9px] font-bold text-blue-600 uppercase mb-1">Timeline Impact</p><p>{narrative.timeline_impact}</p></div>
                    )}
                    {narrative.recommendations?.length > 0 && (
                      <div>
                        <p className="text-[9px] font-bold text-violet-600 uppercase mb-1">Recommendations</p>
                        <ul className="list-disc pl-4 space-y-0.5">{narrative.recommendations.map((r) => <li key={r.slice(0, 40)}>{r}</li>)}</ul>
                      </div>
                    )}
                    {narrative.confidence_notes && (
                      <div className="bg-slate-50 rounded-md p-2 text-[10px] text-slate-500"><p className="text-[9px] font-bold text-slate-400 uppercase mb-1">Confidence Notes</p><p>{narrative.confidence_notes}</p></div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
